import { useLazyQuery, useMutation } from '@apollo/client';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Card } from '@ovComponents/2-component/card/card';
import { Checkbox } from '@ovComponents/2-component/checkbox/checkbox';
import { Dialog } from '@ovComponents/2-component/dialog/dialog';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { Tooltip } from '@ovComponents/2-component/tooltip/tooltip';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { dollars2centsDecimal } from '@utils/dollars2cents';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FETCH_OPTIMAL_PORTFOLIO,
  FETCH_PORTFOLIO_OPTIMIZER_STATUS,
} from '../queries';
import {
  BLOTTER_BADGE_SX,
  BOLD_SX,
  BORDER_LEFT,
  BORDER_RIGHT,
  BORDERED_BOX_SX,
  LABEL_SX,
  LABEL_UPPER_SX,
  MICRO_SX,
  SECTION_HEADER_SX,
  TABULAR_SX,
  TRUNCATE_SX,
  VALUE_SX,
} from '../styles';
import type {
  AssetGroup,
  Currency,
  SubTradeRequest,
  TradeEntry,
} from '../types';
import {
  buildGrid,
  classifyGroup,
  driftColor,
  fmtFx,
  fmtPct,
  fmtQty,
  formatMoney,
  hexAlpha,
  type SysColors,
  tradeSideColor,
  valueColor,
} from '../utils';
import {
  GROUP_LABELS,
  GROUP_ORDER,
  POS_COLS,
  RES_COLS,
} from '../utils/constants';
import {
  CANCEL_SUB_TRADE_REQUESTS_BY_ID,
  RUN_PORTFOLIO_OPTIMIZER,
  VERIFY_SUB_TRADE_REQUESTS,
} from '../utils/mutations';
import SAColumnHeader, { AccountSummaryStrip } from './saColumnHeader';
import { SegmentedToggle, TogglePill } from './segmentedToggle';
import {
  CollapsibleHeader,
  ColumnHeader,
  SideBadge,
  SortableColumnHeader,
} from './shared';
import TradePreviewDialog from './tradePreviewDialog';

const StatusPill = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color: any;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: 0.75,
      py: '3px',
      borderLeft: '2px solid',
      borderColor: color,
    }}
  >
    <Typography
      variant='labelSmall'
      sx={{ ...VALUE_SX, fontWeight: 700, color, lineHeight: 1 }}
    >
      {value}
    </Typography>
    <Typography
      variant='labelSmall'
      sx={{
        fontSize: '0.65rem',
        fontWeight: 500,
        color,
        opacity: 0.8,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
  </Box>
);

type SubAccountInfo = {
  id: string;
  label: string;
  accountType: string;
  currency: Currency;
  accountBaseCurrency?: Currency;
  marketValueCents: number;
  holdingsMap: Map<string, any>;
  cashCents: number;
  cashAvailableForTradeCents: number;
  expectedCashCents: number;
};

type EnrichedHolding = {
  idx: number;
  key: string;
  group: AssetGroup;
  isCash: boolean;
  ticker: string;
  securityName: string;
  currency: Currency;
  quantity: number;
  localValueCents: number; // qty * price, in holding ccy
  fxRate: number;
  baseValueCents: number; // portfolio base ccy
  curPct: number;
  tgtPct: number;
  grpPct: number;
  driftPct: number;
  targetValueCents: number;
  financialProduct: any;
  raw: any; // original holding
};

type SortField =
  | 'security'
  | 'baseValue'
  | 'curPct'
  | 'tgtPct'
  | 'drift'
  | 'postValue'
  | 'postPct';

const PENDING_GRID = '28px 1fr 56px 88px 88px 64px 56px 36px';
const ACTIVITY_GRID = '1fr 40px minmax(80px,1fr) minmax(80px,1fr)';

type TradeBlotterProps = {
  portfolioReport: any;
  onClose: () => void;
  onGenerateTrades: (trades: any[]) => void;
  activeSubTradeRequests?: SubTradeRequest[];
  onRefreshActiveSTRs?: () => void;
};

const TradeBlotter = ({
  portfolioReport,
  onClose,
  onGenerateTrades,
  activeSubTradeRequests = [],
  onRefreshActiveSTRs,
}: TradeBlotterProps) => {
  const { activeOrganization } = useContext(UserContext);
  const allowPendingTransactions =
    activeOrganization?.allowPendingTransactions ?? false;
  const { sys } = useThemeTokens();
  const c = sys.color as SysColors;
  const sysForShared = { color: c };
  const OUTCOME_BORDER_LEFT = {
    borderLeft: '2px solid',
    borderColor: sys.color.outline,
  } as const;
  const SA_SEPARATOR = {
    borderRight: '1px solid',
    borderColor: sys.color.outline,
  } as const;

  const baseCurrency: string = portfolioReport.baseCurrency || 'CAD';
  const [tradeEntries, setTradeEntries] = useState<Map<string, TradeEntry>>(
    new Map(),
  );
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [activityOpen, setActivityOpen] = useState(false);
  const [activeTradesOpen, setActiveTradesOpen] = useState(
    activeSubTradeRequests.length > 0,
  );
  const [showTradePreview, setShowTradePreview] = useState(false);
  const [showShares, setShowShares] = useState(false);
  const [showSAPost, setShowSAPost] = useState(true);
  const [autoFillKeys, setAutoFillKeys] = useState<Set<string>>(new Set());
  const [autoFillProgress, setAutoFillProgress] = useState(1);
  const autoFillRaf = useRef<number>(0);
  const [lastFlash, setLastFlash] = useState<{
    holdingKey: string;
    side: 'BUY' | 'SELL';
    ts: number;
  } | null>(null);

  // Auto-clear row flash after animation
  useEffect(() => {
    if (!lastFlash) return;
    const timer = setTimeout(() => setLastFlash(null), 600);
    return () => clearTimeout(timer);
  }, [lastFlash]);

  const pendingByProductId = useMemo(() => {
    const map = new Map<
      string,
      { totalCents: number; side: string; state: string; saId: string }[]
    >();
    activeSubTradeRequests.forEach((str) => {
      const pid = str.financialProduct?.id;
      if (!pid) return;
      const pendingStates = allowPendingTransactions
        ? ['INITIATED', 'READY']
        : ['INITIATED', 'READY', 'REQUESTED'];
      if (!pendingStates.includes(str.state)) return;
      const existing = map.get(pid) || [];
      existing.push({
        totalCents: str.moneyAllocatedCents,
        side: str.type?.includes('SELL') ? 'SELL' : 'BUY',
        state: str.state,
        saId: str.subAccount?.id || '',
      });
      map.set(pid, existing);
    });
    return map;
  }, [activeSubTradeRequests, allowPendingTransactions]);

  const isHoldingLocked = useCallback(
    (h: EnrichedHolding, saId: string): boolean => {
      const pid = h.financialProduct?.id;
      if (!pid) return false;
      const pending = pendingByProductId.get(pid);
      if (!pending || pending.length === 0) return false;
      // Lock if there's a pending trade for this product in this specific sub-account (or any SA if aggregate)
      if (saId === '__agg__') return true;
      return pending.some((p) => p.saId === saId || p.saId === '');
    },
    [pendingByProductId],
  );

  const hasAnyPendingTrades = activeSubTradeRequests.length > 0;

  const [runOptimizer] = useMutation(RUN_PORTFOLIO_OPTIMIZER);
  const [optimizerState, setOptimizerState] = useState<
    'idle' | 'running' | 'populating'
  >('idle');
  const optimizerLoading = optimizerState !== 'idle';
  const optimizerPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [fetchOptimizerStatus] = useLazyQuery(
    FETCH_PORTFOLIO_OPTIMIZER_STATUS,
    {
      fetchPolicy: 'no-cache',
    },
  );
  const [fetchOptimalPortfolio] = useLazyQuery(FETCH_OPTIMAL_PORTFOLIO, {
    fetchPolicy: 'no-cache',
  });
  const [verifySubTradeRequests, { loading: verifying }] = useMutation(
    VERIFY_SUB_TRADE_REQUESTS,
  );
  const [cancelSubTradeRequestsById, { loading: cancelling }] = useMutation(
    CANCEL_SUB_TRADE_REQUESTS_BY_ID,
  );

  useEffect(() => {
    return () => {
      if (optimizerPollRef.current) clearInterval(optimizerPollRef.current);
    };
  }, []);

  // Active trade request selection
  const [selectedSTRIds, setSelectedSTRIds] = useState<Set<string>>(new Set());
  const toggleSTRSelect = (id: string) => {
    setSelectedSTRIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const unbatchedSTRs = activeSubTradeRequests.filter(
    (s) => !s.bulkTradeRun?.id,
  );
  const selectAllSTRs = () => {
    if (selectedSTRIds.size === unbatchedSTRs.length) {
      setSelectedSTRIds(new Set());
    } else {
      setSelectedSTRIds(new Set(unbatchedSTRs.map((s) => s.id)));
    }
  };
  const allSTRsSelected =
    selectedSTRIds.size === unbatchedSTRs.length && unbatchedSTRs.length > 0;

  const handleVerifySTRs = async () => {
    if (selectedSTRIds.size === 0) return;
    try {
      await verifySubTradeRequests({
        variables: {
          input: {
            subTradeRequestIds: Array.from(selectedSTRIds),
            organizationId: activeOrganization?.id,
          },
        },
      });
      setSelectedSTRIds(new Set());
      onRefreshActiveSTRs?.();
    } catch (err) {
      console.error('Verify failed:', err);
    }
  };

  const cancelSTRs = async (ids: string[]) => {
    if (!ids.length) return;
    try {
      await cancelSubTradeRequestsById({
        variables: {
          input: {
            subTradeRequestIds: ids,
            organizationId: activeOrganization?.id,
          },
        },
      });
      setSelectedSTRIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      onRefreshActiveSTRs?.();
    } catch (err) {
      console.error('Cancel failed:', err);
    }
  };
  const handleCancelSTR = (id: string) => cancelSTRs([id]);
  const handleCancelSelectedSTRs = () => cancelSTRs(Array.from(selectedSTRIds));

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const toBase = useCallback((cents: number, _ccy: string) => cents, []);

  const canonicalMV =
    portfolioReport.object?.statistics?.marketValueCents ||
    portfolioReport.marketValueCents ||
    0;

  const subAccountInfos: SubAccountInfo[] = useMemo(() => {
    const sas: any[] = portfolioReport.subAccounts || [];
    if (sas.length === 0) {
      return [
        {
          id: '__agg__',
          label: portfolioReport.object?.account?.type || 'Account',
          accountType: portfolioReport.object?.account?.type || '',
          currency: portfolioReport.baseCurrency || 'CAD',
          accountBaseCurrency: portfolioReport.baseCurrency,
          marketValueCents: canonicalMV,
          holdingsMap: new Map(
            (portfolioReport.holdings || []).map((h: any) => [
              h.financialProduct?.id || `cash-${h.currency}`,
              h,
            ]),
          ),
          cashCents: portfolioReport.cashCents || 0,
          cashAvailableForTradeCents:
            portfolioReport.cashAvailableForTradeCents ??
            portfolioReport.cashCents ??
            0,
          expectedCashCents: portfolioReport.expectedCashCents ?? 0,
        },
      ];
    }
    return sas.map((sa: any) => {
      const hMap = new Map<string, any>();
      (sa.holdings || []).forEach((h: any) => {
        hMap.set(h.financialProduct?.id || `cash-${h.currency}`, h);
      });
      return {
        id: sa.subAccount?.id || `sa-${Math.random()}`,
        label: sa.subAccount?.account?.type || 'Sub',
        accountType: sa.subAccount?.account?.type || '',
        currency: sa.currency,
        accountBaseCurrency: sa.accountBaseCurrency || sa.currency,
        marketValueCents: sa.marketValueCents || 0,
        holdingsMap: hMap,
        cashCents: sa.cashCents || 0,
        cashAvailableForTradeCents:
          sa.cashAvailableForTradeCents ?? sa.cashCents ?? 0,
        expectedCashCents: sa.expectedCashCents ?? 0,
      };
    });
  }, [portfolioReport, canonicalMV]);

  const hasPerSAData = useMemo(
    () => subAccountInfos.some((sa) => sa.holdingsMap.size > 0),
    [subAccountInfos],
  );

  /* When per-SA holdings are unavailable fall back to a single aggregate column
     that uses the portfolio-level holdings for values. */
  const displaySAs: SubAccountInfo[] = useMemo(() => {
    if (hasPerSAData) return subAccountInfos;
    return [
      {
        id: '__agg__',
        label: 'Account',
        accountType: '',
        currency: portfolioReport.baseCurrency || 'CAD',
        accountBaseCurrency: portfolioReport.baseCurrency,
        marketValueCents: canonicalMV,
        holdingsMap: new Map(
          (portfolioReport.holdings || []).map((h: any) => [
            h.financialProduct?.id || `cash-${h.currency}`,
            h,
          ]),
        ),
        cashCents: portfolioReport.cashCents || 0,
        cashAvailableForTradeCents:
          portfolioReport.cashAvailableForTradeCents ??
          portfolioReport.cashCents ??
          0,
        expectedCashCents: portfolioReport.expectedCashCents ?? 0,
      },
    ];
  }, [hasPerSAData, subAccountInfos, portfolioReport, canonicalMV]);

  const saCount = displaySAs.length;
  const multiSA = saCount > 1;
  // Trade$ only, or Trade$ + Post$

  const GRID = useMemo(
    () => buildGrid(saCount, multiSA, showSAPost),
    [saCount, multiSA, showSAPost],
  );

  const gridMinWidth =
    110 +
    80 +
    52 +
    52 +
    56 +
    saCount * (multiSA ? (showSAPost ? 230 : 150) : 140) +
    80 +
    56 +
    36;

  const isMultiCurrency = (portfolioReport.originalCurrencies?.length || 0) > 1;

  const allHoldings: EnrichedHolding[] = useMemo(() => {
    const mv = canonicalMV || 1;
    const holdings = (portfolioReport.holdings || []).map(
      (h: any, i: number) => {
        const isCash = !!h.financialProduct?.isCash || !!h.isCashEquivalent;
        const ticker =
          h.financialProduct?.ticker ||
          (isCash ? `CASH-${h.currency}` : 'CASH');
        const group = classifyGroup(h);
        const curPct = ((h.valueCents || 0) / mv) * 100;
        const tgtPct = (h.expectedPercentage || 0) * 100;
        const driftPct = curPct - tgtPct;
        const targetVal = Math.round(
          h.expectedValueCents ?? (h.expectedPercentage || 0) * mv,
        );

        return {
          idx: i,
          key: `${i}-${ticker}`,
          group,
          isCash,
          ticker,
          securityName:
            h.financialProduct?.translatedName?.en ||
            h.financialProduct?.name ||
            (isCash ? 'Cash' : 'Unknown'),
          currency: h.currency as Currency,
          quantity: h.quantity || 0,
          localValueCents: h.valueCents || 0,
          fxRate: 1,
          baseValueCents: h.valueCents || 0,
          curPct,
          tgtPct,
          grpPct: 0, // filled below
          driftPct,
          targetValueCents: targetVal,
          financialProduct: h.financialProduct,
          raw: h,
        };
      },
    );

    // Group percentages
    const gTot: Record<AssetGroup, number> = {
      CASH: 0,
      EQUITIES: 0,
      FIXED_INCOME: 0,
      OTHER: 0,
    };
    holdings.forEach((h: EnrichedHolding) => {
      gTot[h.group] += h.tgtPct;
    });
    holdings.forEach((h: EnrichedHolding) => {
      h.grpPct = gTot[h.group] > 0 ? (h.tgtPct / gTot[h.group]) * 100 : 0;
    });
    return holdings;
  }, [portfolioReport.holdings, canonicalMV]);

  const groupedHoldings = useMemo(() => {
    const groups: Record<AssetGroup, EnrichedHolding[]> = {
      CASH: [],
      EQUITIES: [],
      FIXED_INCOME: [],
      OTHER: [],
    };
    allHoldings.forEach((h) => groups[h.group].push(h));

    if (sortField) {
      const acc = (h: EnrichedHolding): number | string => {
        switch (sortField) {
          case 'security':
            return h.ticker;
          case 'baseValue':
            return h.baseValueCents;
          case 'curPct':
            return h.curPct;
          case 'tgtPct':
            return h.tgtPct;
          case 'drift':
            return h.driftPct;
          default:
            return 0;
        }
      };
      Object.values(groups).forEach((a) =>
        a.sort((x, y) => {
          const av = acc(x);
          const bv = acc(y);
          const c = av < bv ? -1 : av > bv ? 1 : 0;
          return sortDir === 'asc' ? c : -c;
        }),
      );
    }

    return GROUP_ORDER.filter((g) => groups[g].length > 0).map((g) => ({
      group: g,
      label: GROUP_LABELS[g],
      holdings: groups[g],
    }));
  }, [allHoldings, sortField, sortDir]);

  const getSAHolding = (sa: SubAccountInfo, h: EnrichedHolding) => {
    const pid = h.financialProduct?.id || `cash-${h.currency}`;
    return sa.holdingsMap.get(pid);
  };

  const canSAHoldCurrency = (sa: SubAccountInfo, ccy: Currency) => {
    if (!sa.accountBaseCurrency) return true;
    let hasCcy = false;
    sa.holdingsMap.forEach((v) => {
      if (v.currency === ccy) hasCcy = true;
    });
    if (hasCcy) return true;
    return sa.accountBaseCurrency === ccy || sa.currency === ccy;
  };

  const holdingTradeTotal = useCallback(
    (holdingKey: string): number => {
      let t = 0;
      displaySAs.forEach((sa) => {
        const te = tradeEntries.get(`${sa.id}:${holdingKey}`);
        if (te) t += te.tradeAmountCents;
      });
      return t;
    },
    [tradeEntries, displaySAs],
  );

  const totals = useMemo(() => {
    let securitiesVal = 0;
    let securitiesPostVal = 0;
    allHoldings.forEach((h) => {
      const base = toBase(h.baseValueCents, h.currency);
      const trade = toBase(holdingTradeTotal(h.key), h.currency);
      securitiesVal += base;
      securitiesPostVal += base + trade;
    });
    const cur = canonicalMV;
    // Compute remaining cash inline: base cash - net trades per SA
    let totalRemainingCash = 0;
    displaySAs.forEach((sa) => {
      let netDelta = 0;
      allHoldings.forEach((h) => {
        if (h.isCash) return;
        const te = tradeEntries.get(`${sa.id}:${h.key}`);
        if (te) netDelta += te.tradeAmountCents;
      });
      totalRemainingCash += sa.cashAvailableForTradeCents - netDelta;
    });
    const postC = securitiesPostVal + totalRemainingCash;
    return { curC: cur, trC: securitiesPostVal - securitiesVal, postC };
  }, [allHoldings, holdingTradeTotal, canonicalMV, displaySAs, tradeEntries]);

  const tradeCount = useMemo(
    () =>
      Array.from(tradeEntries.values()).filter((e) => e.tradeAmountCents !== 0)
        .length,
    [tradeEntries],
  );
  const buyCount = useMemo(
    () =>
      Array.from(tradeEntries.values()).filter((e) => e.tradeAmountCents > 0)
        .length,
    [tradeEntries],
  );
  const sellCount = useMemo(
    () =>
      Array.from(tradeEntries.values()).filter((e) => e.tradeAmountCents < 0)
        .length,
    [tradeEntries],
  );

  const handleTradeChange = (
    saId: string,
    holdingKey: string,
    h: EnrichedHolding,
    side: 'BUY' | 'SELL',
    value: string,
  ) => {
    const mk = `${saId}:${holdingKey}`;
    const clean = value.replace(/[^0-9.]/g, '');
    const next = new Map(tradeEntries);
    if (clean === '' || clean === '0') {
      next.delete(mk);
      setTradeEntries(next);
      return;
    }
    const num = parseFloat(clean);
    if (isNaN(num)) return;
    const rawCents = dollars2centsDecimal(num);
    const signedCents =
      side === 'SELL'
        ? rawCents.abs().neg().toNumber()
        : rawCents.abs().toNumber();
    next.set(mk, {
      holdingIndex: h.idx,
      ticker: h.ticker,
      tradeAmountCents: signedCents,
      currency: h.currency,
      rawInput: clean,
      side,
      subAccountId: saId,
    });
    setTradeEntries(next);
    // Trigger row flash
    setLastFlash({ holdingKey, side, ts: Date.now() });
  };

  const toggleSide = (saId: string, holdingKey: string, h: EnrichedHolding) => {
    const mk = `${saId}:${holdingKey}`;
    const te = tradeEntries.get(mk);
    const next = new Map(tradeEntries);
    if (!te || te.tradeAmountCents === 0) {
      const cur = te?.side || (h.driftPct < 0 ? 'BUY' : 'SELL');
      const ns: 'BUY' | 'SELL' = cur === 'BUY' ? 'SELL' : 'BUY';
      next.set(mk, {
        holdingIndex: h.idx,
        ticker: h.ticker,
        tradeAmountCents: 0,
        currency: h.currency,
        rawInput: '',
        side: ns,
        subAccountId: saId,
      });
    } else {
      next.set(mk, {
        ...te,
        tradeAmountCents: -te.tradeAmountCents,
        side: te.side === 'BUY' ? 'SELL' : 'BUY',
      });
    }
    setTradeEntries(next);
  };

  const handleAutoFill = () => {
    const next = new Map(tradeEntries);

    // Phase 1: Compute all desired trades into a temporary map
    const desired = new Map<string, TradeEntry>();

    allHoldings.forEach((h) => {
      if (h.isCash) return;
      // Match existing portfolio blotter: compare expectedValueCents vs valueCents
      // directly (both from same API response, same currency units)
      const currentCents = h.raw.valueCents || 0;
      const targetCents = h.targetValueCents;
      if (targetCents === currentCents) return;

      const delta = Math.round(targetCents - currentCents);
      if (delta === 0) return;
      const side: 'BUY' | 'SELL' = delta > 0 ? 'BUY' : 'SELL';
      const absDelta = Math.abs(delta);

      if (displaySAs.length > 1) {
        let totalV = 0;
        const vals: number[] = [];
        displaySAs.forEach((sa) => {
          const v = getSAHolding(sa, h)?.valueCents || 0;
          vals.push(v);
          totalV += v;
        });
        // For buys when no SA holds the security, distribute by SA market value
        let totalMV = 0;
        if (totalV === 0 && side === 'BUY') {
          displaySAs.forEach((sa) => {
            totalMV += sa.marketValueCents || 0;
          });
        }
        displaySAs.forEach((sa, i) => {
          if (isHoldingLocked(h, sa.id)) return;
          if (next.has(`${sa.id}:${h.key}`)) return;
          if (!canSAHoldCurrency(sa, h.currency)) return;
          // Sells: only allocate to SAs that actually hold the security
          if (side === 'SELL' && vals[i] <= 0) return;
          const prop =
            totalV > 0
              ? vals[i] / totalV
              : totalMV > 0
                ? (sa.marketValueCents || 0) / totalMV
                : 1 / displaySAs.length;
          let saDelta = Math.round(delta * prop);
          // Cap sells to what the SA actually holds (prevent negative positions)
          if (side === 'SELL' && Math.abs(saDelta) > vals[i]) {
            saDelta = -vals[i];
          }
          if (saDelta === 0) return;
          desired.set(`${sa.id}:${h.key}`, {
            holdingIndex: h.idx,
            ticker: h.ticker,
            tradeAmountCents: saDelta,
            currency: h.currency,
            rawInput: (Math.abs(saDelta) / 100).toFixed(2),
            side,
            subAccountId: sa.id,
          });
        });
      } else {
        const saId = displaySAs[0]?.id || '__agg__';
        if (isHoldingLocked(h, saId)) return;
        if (next.has(`${saId}:${h.key}`)) return;
        desired.set(`${saId}:${h.key}`, {
          holdingIndex: h.idx,
          ticker: h.ticker,
          tradeAmountCents: delta,
          currency: h.currency,
          rawInput: (absDelta / 100).toFixed(2),
          side,
          subAccountId: saId,
        });
      }
    });

    // Phase 2: Constrain buys per sub-account to available cash + sell proceeds
    displaySAs.forEach((sa) => {
      let sellProceeds = 0;
      let totalBuyCost = 0;
      const buyKeys: string[] = [];

      desired.forEach((trade, key) => {
        if (trade.subAccountId !== sa.id) return;
        if (trade.side === 'SELL') {
          sellProceeds += Math.abs(trade.tradeAmountCents);
        } else {
          totalBuyCost += trade.tradeAmountCents;
          buyKeys.push(key);
        }
      });

      const buyingPower = sa.cashAvailableForTradeCents + sellProceeds;

      if (totalBuyCost > buyingPower) {
        if (buyingPower <= 0) {
          buyKeys.forEach((key) => desired.delete(key));
        } else {
          const scale = buyingPower / totalBuyCost;
          buyKeys.forEach((key) => {
            const trade = desired.get(key)!;
            const scaled = Math.round(trade.tradeAmountCents * scale);
            if (scaled === 0) {
              desired.delete(key);
            } else {
              trade.tradeAmountCents = scaled;
              trade.rawInput = (Math.abs(scaled) / 100).toFixed(2);
            }
          });
        }
      }
    });

    // Phase 2.5: Remove dust trades (<= $1.00) — not worth executing
    desired.forEach((trade, key) => {
      if (Math.abs(trade.tradeAmountCents) <= 100) desired.delete(key);
    });

    // Phase 3: Merge constrained trades into the entries
    const newKeys = new Set<string>();
    desired.forEach((trade, key) => {
      const prev = next.get(key);
      if (!prev || prev.tradeAmountCents === 0) newKeys.add(key);
      next.set(key, trade);
    });

    setTradeEntries(next);

    // Animate newly filled values from 0 to final
    if (newKeys.size > 0) {
      cancelAnimationFrame(autoFillRaf.current);
      setAutoFillKeys(newKeys);
      setAutoFillProgress(0);
      const start = performance.now();
      const duration = 350;
      const tick = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        const ease = 1 - (1 - t) ** 3;
        setAutoFillProgress(ease);
        if (t < 1) {
          autoFillRaf.current = requestAnimationFrame(tick);
        } else {
          setAutoFillKeys(new Set());
        }
      };
      autoFillRaf.current = requestAnimationFrame(tick);
    }
  };

  const populateFromOptimalPortfolio = useCallback(async () => {
    setOptimizerState('populating');
    try {
      const res = await fetchOptimalPortfolio({
        variables: { input: { optimalPortfolioId: portfolioReport.id } },
      });
      const optimalPortfolio =
        res.data?.fetchOptimalPortfolio?.optimalPortfolio;
      if (!optimalPortfolio) {
        setOptimizerState('idle');
        return;
      }

      const next = new Map(tradeEntries);
      const newKeys = new Set<string>();
      const holdings = portfolioReport.holdings || [];
      const holdingByProductId = new Map<string, number>();
      holdings.forEach((h: any, i: number) => {
        if (h.financialProduct?.id)
          holdingByProductId.set(h.financialProduct.id, i);
      });

      for (const sa of optimalPortfolio.subAccounts || []) {
        const saId = sa.subAccount;
        if (!saId) continue;

        for (const oh of sa.optimalHoldings || []) {
          const allocCents = Math.round(
            sa.preTradeHoldingAllocations?.[oh.index] ?? 0,
          );
          if (allocCents === 0) continue;

          const fpId = oh.financialProduct;
          const holdingIdx = holdingByProductId.get(fpId);
          if (holdingIdx === undefined) continue;

          const holding = holdings[holdingIdx];
          if (!holding || holding.financialProduct?.isCash) continue;

          const ticker = holding.financialProduct?.ticker || '';
          const currency =
            holding.currency || portfolioReport.baseCurrency || 'CAD';
          const side: 'BUY' | 'SELL' = allocCents > 0 ? 'BUY' : 'SELL';
          const absCents = Math.abs(allocCents);

          if (absCents <= 100) continue;

          const matchingSA = displaySAs.find((s) => s.id === saId);
          const resolvedSAId = matchingSA
            ? saId
            : displaySAs[0]?.id || '__agg__';
          const key = `${resolvedSAId}:${holding.financialProduct?.id || `cash-${holding.currency}`}`;

          if (next.has(key)) continue;
          if (
            matchingSA &&
            isHoldingLocked(allHoldings[holdingIdx], resolvedSAId)
          )
            continue;

          newKeys.add(key);
          next.set(key, {
            holdingIndex: holdingIdx,
            ticker,
            tradeAmountCents: allocCents,
            currency: currency as Currency,
            rawInput: (absCents / 100).toFixed(2),
            side,
            subAccountId: resolvedSAId,
          });
        }
      }

      setTradeEntries(next);

      if (newKeys.size > 0) {
        cancelAnimationFrame(autoFillRaf.current);
        setAutoFillKeys(newKeys);
        setAutoFillProgress(0);
        const start = performance.now();
        const duration = 350;
        const tick = (now: number) => {
          const t = Math.min((now - start) / duration, 1);
          const ease = 1 - (1 - t) ** 3;
          setAutoFillProgress(ease);
          if (t < 1) {
            autoFillRaf.current = requestAnimationFrame(tick);
          } else {
            setAutoFillKeys(new Set());
          }
        };
        autoFillRaf.current = requestAnimationFrame(tick);
      }
    } catch (e) {
      console.error('Failed to fetch optimal portfolio:', e);
    }
    setOptimizerState('idle');
  }, [
    fetchOptimalPortfolio,
    portfolioReport,
    tradeEntries,
    displaySAs,
    allHoldings,
    isHoldingLocked,
    autoFillRaf,
  ]);

  const handleOptimize = async () => {
    if (optimizerLoading) return;
    setOptimizerState('running');
    try {
      const res = await runOptimizer({
        variables: {
          input: {
            organizationId: activeOrganization?.id,
            portfolioReportId: portfolioReport.id,
            dryRun: true,
          },
        },
      });
      const optimizerId =
        res.data?.runPortfolioOptimizer?.portfolioOptimizer?.id;
      if (!optimizerId) {
        setOptimizerState('idle');
        return;
      }

      if (optimizerPollRef.current) clearInterval(optimizerPollRef.current);
      optimizerPollRef.current = setInterval(async () => {
        try {
          const pollRes = await fetchOptimizerStatus({
            variables: { input: { portfolioOptimizerId: optimizerId } },
          });
          const state =
            pollRes.data?.fetchPortfolioOptimizer?.portfolioOptimizer?.state;
          if (['COMPLETED', 'CANCELED', 'FAILED'].includes(state)) {
            if (optimizerPollRef.current)
              clearInterval(optimizerPollRef.current);
            optimizerPollRef.current = null;
            if (state === 'COMPLETED') {
              await populateFromOptimalPortfolio();
            } else {
              setOptimizerState('idle');
            }
          }
        } catch {
          if (optimizerPollRef.current) clearInterval(optimizerPollRef.current);
          optimizerPollRef.current = null;
          setOptimizerState('idle');
        }
      }, 3000);
    } catch (e) {
      console.error('Auto-rebalance failed:', e);
      setOptimizerState('idle');
    }
  };

  const warnings = useMemo(() => {
    const w: string[] = [];
    const pBaseCcy = baseCurrency;

    const totalCashAvailable = displaySAs.reduce(
      (sum, sa) => sum + Math.max(0, sa.cashAvailableForTradeCents || 0),
      0,
    );

    let totalBuysBase = 0;
    let totalSellsBase = 0;
    Array.from(tradeEntries.values()).forEach((te) => {
      const amtBase = toBase(Math.abs(te.tradeAmountCents), te.currency);
      if (te.tradeAmountCents > 0) totalBuysBase += amtBase;
      else totalSellsBase += amtBase;
    });

    const buyingPower = totalCashAvailable + totalSellsBase;
    if (totalBuysBase > buyingPower + 100) {
      const shortfall = totalBuysBase - buyingPower;
      w.push(`Insufficient cash — short ${formatMoney(shortfall)} ${pBaseCcy}`);
    }

    return w;
  }, [allHoldings, tradeEntries, portfolioReport, canonicalMV]);

  const remainingCashBySA: Map<string, number> = useMemo(() => {
    const m = new Map<string, number>();
    displaySAs.forEach((sa) => {
      const baseCash = sa.cashAvailableForTradeCents;
      let netDelta = 0;
      allHoldings.forEach((h) => {
        if (h.isCash) return;
        const te = tradeEntries.get(`${sa.id}:${h.key}`);
        if (te) netDelta += te.tradeAmountCents; // +buy / -sell
      });

      m.set(sa.id, baseCash - netDelta);
    });
    return m;
  }, [displaySAs, allHoldings, tradeEntries]);

  const isAnySAOverdrawn = useMemo(
    () => Array.from(remainingCashBySA.values()).some((v) => v < -100),
    [remainingCashBySA],
  );

  const activityBySA = useMemo(() => {
    if (tradeCount === 0) return [];
    return displaySAs.map((sa) => {
      const entries: {
        ticker: string;
        name: string;
        side: string;
        amountCents: number;
        balanceCents: number;
      }[] = [];
      // net cash delta
      let cashDelta = 0;
      allHoldings.forEach((h) => {
        if (h.isCash) return;
        const te = tradeEntries.get(`${sa.id}:${h.key}`);
        if (te) cashDelta -= te.tradeAmountCents;
      });
      entries.push({
        ticker: 'Cash',
        name: 'Cash',
        side: '—',
        amountCents: cashDelta,
        balanceCents: sa.cashCents + cashDelta,
      });
      // securities
      allHoldings.forEach((h) => {
        if (h.isCash) return;
        const te = tradeEntries.get(`${sa.id}:${h.key}`);
        if (!te || te.tradeAmountCents === 0) return;
        const saH = getSAHolding(sa, h);
        entries.push({
          ticker: h.ticker,
          name: h.securityName,
          side: te.tradeAmountCents > 0 ? 'BUY' : 'SELL',
          amountCents: te.tradeAmountCents,
          balanceCents: (saH?.valueCents || 0) + te.tradeAmountCents,
        });
      });
      return { sa, entries };
    });
  }, [displaySAs, allHoldings, tradeEntries, tradeCount]);

  const tradePreviewData = useMemo(() => {
    if (!showTradePreview) return null;
    const activeTrades = Array.from(tradeEntries.values()).filter(
      (e) => e.tradeAmountCents !== 0,
    );
    if (activeTrades.length === 0) return null;

    // Group by sub-account
    const bySA = new Map<
      string,
      {
        label: string;
        accountType: string;
        currency: string;
        trades: typeof activeTrades;
      }
    >();
    for (const t of activeTrades) {
      const saId = t.subAccountId || '__agg__';
      if (!bySA.has(saId)) {
        const sa = displaySAs.find((s) => s.id === saId);
        bySA.set(saId, {
          label: sa?.label || 'Account',
          accountType: sa?.accountType || '',
          currency: sa?.currency || 'CAD',
          trades: [],
        });
      }
      bySA.get(saId)!.trades.push(t);
    }

    let totalBuyCents = 0;
    let totalSellCents = 0;
    for (const t of activeTrades) {
      if (t.tradeAmountCents > 0) totalBuyCents += t.tradeAmountCents;
      else totalSellCents += Math.abs(t.tradeAmountCents);
    }

    return {
      groups: Array.from(bySA.entries()).map(([saId, data]) => ({
        saId,
        ...data,
      })),
      totalBuyCents,
      totalSellCents,
      totalTrades: activeTrades.length,
      netCents: totalBuyCents - totalSellCents,
    };
  }, [showTradePreview, tradeEntries, displaySAs]);

  const isGoal = portfolioReport.modelPortfolioType === 'GOAL';
  const name = isGoal
    ? portfolioReport.object?.name || 'Goal'
    : `${portfolioReport.object?.account?.user?.firstName || ''} ${portfolioReport.object?.account?.user?.lastName || ''}`.trim() ||
      'Portfolio';
  const subtitle = isGoal
    ? portfolioReport.object?.type || ''
    : portfolioReport.object?.account?.type || '';
  const modelName = portfolioReport.modelPortfolio?.translatedName?.en || '';

  const handleSort = (f: SortField) => {
    if (sortField === f) {
      if (sortDir === 'asc') setSortDir('desc');
      else setSortField(null), setSortDir('asc');
    } else setSortField(f), setSortDir('asc');
  };

  const SACellTrade = ({ value }: { value: number }) => (
    <Box px={0.5} textAlign='right'>
      <Typography
        variant='labelSmall'
        sx={{ ...BOLD_SX, color: valueColor(c, value) }}
      >
        {value !== 0 ? formatMoney(Math.abs(value)) : '—'}
      </Typography>
    </Box>
  );
  const SAPostCell = ({
    value,
    showBorder,
  }: {
    value: number;
    showBorder: boolean;
  }) => (
    <Box
      textAlign='left'
      sx={{
        pl: 0.5,
        pr: showBorder ? 1 : 0.5,
        ...(showBorder ? SA_SEPARATOR : {}),
      }}
    >
      <Typography variant='labelSmall' sx={BOLD_SX}>
        {formatMoney(value)}
      </Typography>
    </Box>
  );

  const LockedTradeCell = ({
    pendingAmt,
    pendingSide,
  }: {
    pendingAmt: number;
    pendingSide: string;
  }) => (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='center'
      gap={0.25}
      sx={{ height: 24 }}
    >
      <LockIcon sx={{ fontSize: 10, color: sys.color.onDisabled }} />
      <Typography
        variant='labelSmall'
        sx={{
          ...LABEL_SX,
          color: tradeSideColor(c, pendingSide === 'BUY'),
          opacity: 0.7,
        }}
      >
        {pendingAmt > 0 ? formatMoney(pendingAmt) : '—'}
      </Typography>
    </Box>
  );

  const TradeEntryRow = ({
    ent,
    isLast,
  }: {
    ent: {
      name: string;
      side: string;
      amountCents: number;
      balanceCents: number;
    };
    isLast: boolean;
  }) => (
    <Box
      display='grid'
      gridTemplateColumns={ACTIVITY_GRID}
      columnGap='4px'
      sx={{
        px: 1,
        py: 0.2,
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: sys.color.outlineVariant,
        borderLeft: '2px solid',
        borderLeftColor:
          ent.side === '—'
            ? 'transparent'
            : tradeSideColor(c, ent.side === 'BUY'),
        transition: 'background 0.1s ease',
        '&:hover': { bgcolor: sys.color.neutralHover },
      }}
    >
      <Typography variant='labelSmall' sx={{ ...TRUNCATE_SX, ...LABEL_SX }}>
        {ent.name}
      </Typography>
      <Box textAlign='center'>
        {ent.side === '—' ? (
          <Typography variant='labelSmall' sx={{ color: sys.color.onDisabled }}>
            —
          </Typography>
        ) : (
          <SideBadge
            isBuy={ent.side === 'BUY'}
            label={ent.side}
            sys={sysForShared}
          />
        )}
      </Box>
      <Box textAlign='right'>
        <Typography
          variant='labelSmall'
          sx={{
            ...TABULAR_SX,
            fontWeight: ent.amountCents !== 0 ? 600 : 400,
            color: valueColor(c, ent.amountCents),
          }}
        >
          {ent.amountCents !== 0 ? formatMoney(Math.abs(ent.amountCents)) : '—'}
        </Typography>
      </Box>
      <Box textAlign='right'>
        <Typography
          variant='labelSmall'
          sx={{ ...TABULAR_SX, fontWeight: 600 }}
        >
          {formatMoney(ent.balanceCents)}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      sx={{
        // ovComponents Dialog does not expose paperProps — MUI class selector required for sizing
        '.MuiDialog-paper': {
          width: { xs: '98%', md: '96%', lg: '94%' },
          maxWidth: saCount <= 1 ? 1050 : saCount === 2 ? 1300 : 1600,
          maxHeight: '94vh',
          bgcolor: sys.color.background,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...TABULAR_SX,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          zIndex: 5,
          position: 'relative',
        }}
      >
        {/* Identity row */}
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: sys.color.outlineVariant,
          }}
          display='flex'
          alignItems='center'
          justifyContent='space-between'
        >
          <Box display='flex' alignItems='center' gap={1} minWidth={0}>
            <Typography
              variant='bodySmall'
              sx={{ fontWeight: 700, ...TRUNCATE_SX }}
            >
              {name}
              {subtitle ? ` — ${subtitle}` : ''}
            </Typography>
            {modelName && (
              <Box
                sx={{
                  px: 0.75,
                  py: '2px',
                  borderRadius: '4px',
                  bgcolor: sys.color.neutralHover,
                  border: '1px solid',
                  borderColor: sys.color.outlineVariant,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...LABEL_SX,
                    color: sys.color.onSurfaceVariant,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {modelName}
                </Typography>
              </Box>
            )}
            {isMultiCurrency && (
              <Tooltip
                title={`Multi-currency: ${portfolioReport.originalCurrencies?.join(', ') || ''} (base ${baseCurrency})`}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    px: 0.5,
                    py: '2px',
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: sys.color.outlineVariant,
                    flexShrink: 0,
                  }}
                >
                  {(portfolioReport.originalCurrencies || []).map(
                    (ccy: string) => (
                      <Typography
                        key={ccy}
                        variant='labelSmall'
                        sx={{
                          ...MICRO_SX,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color:
                            ccy === baseCurrency
                              ? sys.color.primary
                              : sys.color.onSurfaceVariant,
                        }}
                      >
                        {ccy}
                      </Typography>
                    ),
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
          <IconButton onClick={onClose} size='small' label='Close'>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        {/* Actions row */}
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: sys.color.outlineVariant,
          }}
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
        >
          <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
            <SegmentedToggle
              leftLabel='$'
              rightLabel='#'
              isRightActive={showShares}
              onToggle={() => setShowShares((v) => !v)}
            />
            {multiSA && (
              <TogglePill
                label='Post'
                isActive={showSAPost}
                onToggle={() => setShowSAPost((v) => !v)}
              />
            )}
            <Button
              label='AutoFill to Target'
              variant='outlined'
              size='sm'
              onClick={handleAutoFill}
            />
            <Button
              label={
                optimizerState === 'running'
                  ? 'Optimizing…'
                  : optimizerState === 'populating'
                    ? 'Populating Trades…'
                    : 'Auto-Rebalance'
              }
              variant='outlined'
              size='sm'
              disabled={optimizerLoading}
              onClick={handleOptimize}
            />
          </Box>
          <Box display='flex' alignItems='center' gap={1}>
            <Button
              label={
                tradeCount > 0
                  ? `Review ${tradeCount} Trade${tradeCount !== 1 ? 's' : ''}`
                  : 'Generate Trades'
              }
              variant='filled'
              size='sm'
              disabled={tradeCount === 0 || isAnySAOverdrawn}
              onClick={() => setShowTradePreview(true)}
            />
            <Button
              label='Clear'
              variant='text'
              size='sm'
              disabled={tradeCount === 0}
              onClick={() => setTradeEntries(new Map())}
            />
          </Box>
        </Box>
      </Box>

      <AccountSummaryStrip
        accounts={displaySAs.map((sa) => ({
          id: sa.id,
          label: sa.label,
          accountType: sa.accountType,
          accountBaseCurrency: sa.accountBaseCurrency,
          expectedCashCents: sa.expectedCashCents,
          cashAvailableForTradeCents: sa.cashAvailableForTradeCents,
          marketValueCents: sa.marketValueCents,
          remainingCash:
            remainingCashBySA.get(sa.id) ?? sa.cashAvailableForTradeCents,
          hasTradesInSA: Array.from(tradeEntries.entries()).some(
            ([k, te]) => k.startsWith(`${sa.id}:`) && te.tradeAmountCents !== 0,
          ),
        }))}
        multiSA={multiSA}
      />

      {(warnings.length > 0 || hasAnyPendingTrades) && (
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: sys.color.outlineVariant,
            bgcolor: sys.color.warningVariant,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <WarningAmberIcon
            sx={{ fontSize: 14, color: sys.color.warning, flexShrink: 0 }}
          />
          <Box>
            {hasAnyPendingTrades && (
              <Typography
                variant='labelSmall'
                sx={{ fontWeight: 600, color: sys.color.warning }}
              >
                {activeSubTradeRequests.length} pending trade
                {activeSubTradeRequests.length !== 1 ? 's' : ''} — locked
                positions can be freed by cancelling the trade below
              </Typography>
            )}
            {warnings.map((w, i) => (
              <Typography
                variant='labelSmall'
                key={i}
                sx={{ fontWeight: 600, color: sys.color.warning }}
              >
                {w}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Card sx={{ overflowX: 'auto', borderRadius: 0, boxShadow: 'none' }}>
          <Box sx={{ minWidth: gridMinWidth }}>
            <Box
              display='grid'
              gridTemplateColumns={GRID}
              gap={0}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                bgcolor: sys.color.background,
                borderBottom: '2px solid',
                borderColor: sys.color.onSurface,
                px: 1.5,
                py: 0.375,
                boxShadow: `0 1px 0 ${sys.color.neutralHover}`,
              }}
            >
              <SortableColumnHeader
                field='security'
                label='Security'
                align='left'
                sortField={sortField ?? ''}
                sortDir={sortDir}
                onSort={(f) => handleSort(f as SortField)}
                sys={sysForShared}
              />
              <SortableColumnHeader
                field='baseValue'
                label={showShares ? 'Qty' : 'Current $'}
                sortField={sortField ?? ''}
                sortDir={sortDir}
                onSort={(f) => handleSort(f as SortField)}
                sys={sysForShared}
              />
              <SortableColumnHeader
                field='curPct'
                label='Wt%'
                sortField={sortField ?? ''}
                sortDir={sortDir}
                onSort={(f) => handleSort(f as SortField)}
                sys={sysForShared}
              />
              <SortableColumnHeader
                field='tgtPct'
                label='Tgt%'
                sortField={sortField ?? ''}
                sortDir={sortDir}
                onSort={(f) => handleSort(f as SortField)}
                sys={sysForShared}
              />
              <SortableColumnHeader
                field='drift'
                label='Drift'
                sortField={sortField ?? ''}
                sortDir={sortDir}
                onSort={(f) => handleSort(f as SortField)}
                sys={sysForShared}
              />
              {displaySAs.map((sa, idx) => (
                <Fragment key={sa.id}>
                  <Box
                    sx={{
                      borderLeft: '2px solid',
                      borderColor: sys.color.outline,
                      pl: 0.5,
                    }}
                  >
                    <SAColumnHeader
                      saId={sa.id}
                      label={sa.label}
                      accountType={sa.accountType}
                      accountBaseCurrency={sa.accountBaseCurrency}
                      expectedCashCents={sa.expectedCashCents}
                      cashAvailableForTradeCents={sa.cashAvailableForTradeCents}
                      marketValueCents={sa.marketValueCents}
                      remainingCash={
                        remainingCashBySA.get(sa.id) ??
                        sa.cashAvailableForTradeCents
                      }
                      hasTradesInSA={Array.from(tradeEntries.entries()).some(
                        ([k, te]) =>
                          k.startsWith(`${sa.id}:`) &&
                          te.tradeAmountCents !== 0,
                      )}
                      multiSA={multiSA}
                      isLast={idx >= displaySAs.length - 1}
                      gridColumnStart={1}
                      gridColumnEnd={2}
                      saSeparator={{}}
                      idx={idx}
                    />
                  </Box>
                  {multiSA && showSAPost && (
                    <Box
                      sx={{
                        ...(idx < displaySAs.length - 1 ? SA_SEPARATOR : {}),
                      }}
                    >
                      <ColumnHeader
                        label={showShares ? 'Post Qty' : 'Post $'}
                        color={sys.color.primary}
                      />
                    </Box>
                  )}
                </Fragment>
              ))}
              <Box
                sx={{
                  borderLeft: '2px solid',
                  borderColor: sys.color.outline,
                  pl: 0.5,
                }}
              >
                <SortableColumnHeader
                  field='postValue'
                  label={showShares ? 'Post Qty' : 'Post $'}
                  sortField={sortField ?? ''}
                  sortDir={sortDir}
                  onSort={(f) => handleSort(f as SortField)}
                  sys={sysForShared}
                />
              </Box>
              <SortableColumnHeader
                field='postPct'
                label='Post %'
                sortField={sortField ?? ''}
                sortDir={sortDir}
                onSort={(f) => handleSort(f as SortField)}
                sys={sysForShared}
              />
            </Box>

            {groupedHoldings.map((g) => {
              let gBaseVal = 0;
              let gCurPct = 0;
              let gTgtPct = 0;
              let gDrift = 0;
              let gPostVal = 0;
              const gSABase: number[] = new Array(saCount).fill(0);
              const gSATrade: number[] = new Array(saCount).fill(0);
              const gSAPost: number[] = new Array(saCount).fill(0);

              g.holdings.forEach((h) => {
                gBaseVal += h.baseValueCents;
                gCurPct += h.curPct;
                gTgtPct += h.tgtPct;
                gDrift += h.driftPct;
                const tt = holdingTradeTotal(h.key);
                gPostVal += h.baseValueCents + tt;
                displaySAs.forEach((sa, si) => {
                  const saH = getSAHolding(sa, h);
                  const saVal = saH?.valueCents || 0;
                  const saTrade =
                    tradeEntries.get(`${sa.id}:${h.key}`)?.tradeAmountCents ||
                    0;
                  gSABase[si] += saVal;
                  gSATrade[si] += saTrade;
                  gSAPost[si] += saVal + saTrade;
                });
              });

              const gPostPct =
                canonicalMV > 0 ? (gPostVal / canonicalMV) * 100 : 0;
              return (
                <Fragment key={g.group}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.375,
                      bgcolor: sys.color.neutralHover,
                      borderBottom: '1px solid',
                      borderColor: sys.color.outlineVariant,
                      borderLeft: '3px solid',
                      borderLeftColor: sys.color.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant='labelSmall'
                      sx={{
                        ...LABEL_UPPER_SX,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        color: sys.color.primary,
                      }}
                    >
                      {g.label}
                    </Typography>
                    <Typography
                      variant='labelSmall'
                      sx={{ ...MICRO_SX, color: sys.color.onDisabled }}
                    >
                      {g.holdings.length} holding
                      {g.holdings.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {g.holdings.map((h, hIdx) => {
                    const aggTrade = holdingTradeTotal(h.key);
                    const postVal = h.baseValueCents + aggTrade;
                    const priceCents =
                      h.financialProduct?.currentPriceCents || 0;
                    const postQty =
                      priceCents > 0
                        ? h.quantity + aggTrade / (priceCents * h.fxRate)
                        : h.quantity;
                    const postPct =
                      canonicalMV > 0 ? (postVal / canonicalMV) * 100 : 0;
                    const absDrift = Math.abs(h.driftPct);
                    const driftColorVal = driftColor(c, absDrift);
                    const hasTrade = aggTrade !== 0;
                    const isBuy = aggTrade > 0;

                    // Build tooltip for Current$ hover
                    const tooltipLines: string[] = [];
                    if (!h.isCash) {
                      tooltipLines.push(
                        `${fmtQty(h.quantity)} shares × ${formatMoney(h.financialProduct?.currentPriceCents || 0)} (${h.currency})`,
                      );
                      if (h.fxRate !== 1)
                        tooltipLines.push(`FX: ${fmtFx(h.fxRate)}`);
                      tooltipLines.push(
                        `Local: ${formatMoney(h.localValueCents)}`,
                      );
                    }

                    const rowHasFocus = focusedField
                      ? focusedField.endsWith(`:${h.key}`)
                      : false;

                    // Row flash animation
                    const isFlashing =
                      lastFlash &&
                      lastFlash.holdingKey === h.key &&
                      Date.now() - lastFlash.ts < 600;
                    const flashColor =
                      isFlashing && lastFlash
                        ? lastFlash.side === 'BUY'
                          ? sys.color.positiveVariant
                          : sys.color.negativeVariant
                        : undefined;

                    return (
                      <Box
                        key={h.key}
                        display='grid'
                        gridTemplateColumns={GRID}
                        gap={0}
                        sx={{
                          px: 1.5,
                          py: 0.25,
                          alignItems: 'center',
                          borderBottom: '1px solid',
                          borderColor: sys.color.outlineVariant,
                          borderLeft: '3px solid',
                          borderLeftColor: rowHasFocus
                            ? sys.color.primary
                            : hasTrade
                              ? isBuy
                                ? sys.color.positive
                                : sys.color.negative
                              : 'transparent',
                          bgcolor: rowHasFocus
                            ? sys.color.primaryVariant
                            : hIdx % 2 === 1
                              ? sys.color.background
                              : sys.color.surface,
                          '&:hover': {
                            bgcolor: sys.color.surfaceContainerVariant,
                            ...(!hasTrade
                              ? { borderLeftColor: sys.color.outlineVariant }
                              : {}),
                          },
                          transition:
                            'background 0.15s ease, border-color 0.15s ease',
                          ...(flashColor
                            ? {
                                animation: 'tradeFlash 0.5s ease-out',
                                '@keyframes tradeFlash': {
                                  '0%': { backgroundColor: flashColor },
                                  '100%': { backgroundColor: 'transparent' },
                                },
                              }
                            : {}),
                        }}
                      >
                        <Box
                          overflow='hidden'
                          px={0.5}
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            bgcolor: 'inherit',
                          }}
                        >
                          <Box display='flex' alignItems='center' gap={0.5}>
                            <Typography
                              variant='labelSmall'
                              sx={{ ...BOLD_SX, lineHeight: 1.2 }}
                            >
                              {h.ticker}
                            </Typography>
                            {isMultiCurrency && (
                              <Box
                                sx={{
                                  ...BLOTTER_BADGE_SX,
                                  bgcolor: sys.color.neutralPressed,
                                  lineHeight: 1,
                                  flexShrink: 0,
                                }}
                              >
                                <Typography
                                  variant='labelSmall'
                                  sx={{
                                    ...MICRO_SX,
                                    fontWeight: 600,
                                    color: sys.color.onSurfaceVariant,
                                  }}
                                >
                                  {h.currency}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography
                            variant='labelSmall'
                            sx={{ color: sys.color.onDisabled, ...TRUNCATE_SX }}
                          >
                            {h.securityName}
                          </Typography>
                        </Box>
                        <Tooltip
                          title={
                            tooltipLines.length > 0
                              ? tooltipLines.join(' · ')
                              : showShares
                                ? formatMoney(h.baseValueCents)
                                : `${fmtQty(h.quantity)} shares`
                          }
                          placement='top'
                        >
                          <Box
                            px={0.5}
                            textAlign='right'
                            sx={{ cursor: 'help', opacity: 0.7 }}
                          >
                            <Typography
                              variant='labelSmall'
                              sx={{
                                ...LABEL_SX,
                                fontWeight: h.curPct > 15 ? 600 : 400,
                                color:
                                  h.curPct > 15
                                    ? sys.color.onSurface
                                    : sys.color.onSurfaceVariant,
                              }}
                            >
                              {showShares
                                ? fmtQty(h.quantity)
                                : formatMoney(h.baseValueCents)}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Box px={0.5} textAlign='right'>
                          <Typography
                            variant='labelSmall'
                            sx={{
                              ...LABEL_SX,
                              color:
                                h.curPct > 15
                                  ? sys.color.onSurface
                                  : sys.color.onSurfaceVariant,
                              fontWeight: h.curPct > 15 ? 600 : 400,
                            }}
                          >
                            {fmtPct(h.curPct)}
                          </Typography>
                        </Box>
                        <Box px={0.5} textAlign='right'>
                          <Typography
                            variant='labelSmall'
                            sx={{
                              ...LABEL_SX,
                              color:
                                h.curPct > 15
                                  ? sys.color.onSurface
                                  : sys.color.onSurfaceVariant,
                              fontWeight: absDrift > 2 ? 600 : 400,
                            }}
                          >
                            {fmtPct(h.tgtPct)}
                          </Typography>
                        </Box>
                        {(() => {
                          const projectedDrift = postPct - h.tgtPct;
                          const absProjDrift = Math.abs(projectedDrift);
                          const projDriftColor = driftColor(c, absProjDrift);
                          const activeDrift = hasTrade
                            ? absProjDrift
                            : absDrift;
                          const activeColor = hasTrade
                            ? projDriftColor
                            : driftColorVal;
                          // Heatmap opacity: 0 at 0%, ramps to ~0.18 at 10%+
                          const heatOpacity =
                            Math.min(activeDrift / 10, 1) * 0.18;
                          return (
                            <Box
                              px={0.5}
                              textAlign='right'
                              sx={{
                                ...BORDER_RIGHT,
                                bgcolor: `${activeColor}${Math.round(
                                  heatOpacity * 255,
                                )
                                  .toString(16)
                                  .padStart(2, '0')}`,
                                transition: 'background-color 0.3s ease',
                              }}
                            >
                              <Typography
                                variant='labelSmall'
                                sx={{
                                  fontWeight: activeDrift > 1 ? 700 : 400,
                                  color: activeColor,
                                  transition: 'color 0.3s ease',
                                }}
                              >
                                {hasTrade
                                  ? `${projectedDrift > 0 ? '+' : ''}${projectedDrift.toFixed(1)}`
                                  : `${h.driftPct > 0 ? '+' : ''}${h.driftPct.toFixed(1)}`}
                              </Typography>
                            </Box>
                          );
                        })()}

                        {displaySAs.map((sa, si) => {
                          const saH = getSAHolding(sa, h);
                          const saVal = saH?.valueCents || 0;
                          const locked = isHoldingLocked(h, sa.id);
                          const disabled =
                            h.isCash ||
                            !canSAHoldCurrency(sa, h.currency) ||
                            locked;
                          const mk = `${sa.id}:${h.key}`;
                          const te = tradeEntries.get(mk);
                          const saTradeCents = te?.tradeAmountCents || 0;
                          const saPostVal = Math.max(0, saVal + saTradeCents);
                          const saQty = saH?.quantity || 0;
                          const saPostQty =
                            priceCents > 0
                              ? saQty + saTradeCents / (priceCents * h.fxRate)
                              : saQty;
                          const saHasTrade = saTradeCents !== 0;
                          const saIsBuy = saTradeCents > 0;
                          const saIsSell = saTradeCents < 0;
                          const currentSide: 'BUY' | 'SELL' =
                            te?.side || (h.driftPct < 0 ? 'BUY' : 'SELL');
                          const isAutoFilling =
                            autoFillKeys.has(mk) && autoFillProgress < 1;
                          const displayVal =
                            focusedField === mk
                              ? te?.rawInput || ''
                              : saHasTrade
                                ? (
                                    (Math.abs(saTradeCents) *
                                      (isAutoFilling ? autoFillProgress : 1)) /
                                    100
                                  ).toFixed(2)
                                : '';
                          return (
                            <Fragment key={sa.id}>
                              <Box
                                px={0.25}
                                sx={{
                                  ...(disabled && !locked
                                    ? { opacity: 0.3 }
                                    : {}),
                                }}
                              >
                                {disabled ? (
                                  locked ? (
                                    (() => {
                                      const pid = h.financialProduct?.id;
                                      const pending = pid
                                        ? pendingByProductId.get(pid)
                                        : undefined;
                                      const match = pending?.find(
                                        (p) =>
                                          p.saId === sa.id || p.saId === '',
                                      );
                                      return (
                                        <LockedTradeCell
                                          pendingAmt={
                                            match
                                              ? Math.abs(match.totalCents)
                                              : 0
                                          }
                                          pendingSide={match?.side || 'BUY'}
                                        />
                                      );
                                    })()
                                  ) : (
                                    <Typography
                                      variant='labelSmall'
                                      sx={{
                                        color: sys.color.onDisabled,
                                        textAlign: 'center',
                                      }}
                                    >
                                      —
                                    </Typography>
                                  )
                                ) : (
                                  <Box
                                    className='trade-input-box'
                                    display='flex'
                                    alignItems='center'
                                    sx={{
                                      border: '1px solid',
                                      borderStyle: saHasTrade
                                        ? 'solid'
                                        : 'dashed',
                                      borderColor:
                                        focusedField === mk
                                          ? sys.color.primary
                                          : saHasTrade
                                            ? saIsBuy
                                              ? sys.color.positive
                                              : sys.color.negative
                                            : sys.color.outlineVariant,
                                      borderRadius: '4px',
                                      bgcolor: saHasTrade
                                        ? saIsBuy
                                          ? hexAlpha(
                                              sys.color.positive || '#4caf50',
                                              0.06,
                                            )
                                          : hexAlpha(
                                              sys.color.negative || '#f44336',
                                              0.06,
                                            )
                                        : 'transparent',
                                      overflow: 'hidden',
                                      transition: 'all 0.15s ease',
                                      '&:hover': {
                                        borderColor: saHasTrade
                                          ? undefined
                                          : sys.color.onSurfaceVariant,
                                        borderStyle: 'solid',
                                        '& .trade-clear-btn': { opacity: 1 },
                                      },
                                    }}
                                  >
                                    <Box
                                      onClick={() =>
                                        toggleSide(sa.id, h.key, h)
                                      }
                                      sx={{
                                        width: 22,
                                        minWidth: 22,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        height: 24,
                                        flexShrink: 0,
                                        borderRight: '1px solid',
                                        borderColor: saHasTrade
                                          ? 'transparent'
                                          : sys.color.outlineVariant,
                                        '&:hover': { opacity: 0.7 },
                                      }}
                                    >
                                      <Typography
                                        variant='labelSmall'
                                        sx={{
                                          lineHeight: 1,
                                          fontSize: '0.9rem',
                                          fontWeight: 700,
                                          color:
                                            currentSide === 'BUY'
                                              ? sys.color.positive
                                              : sys.color.negative,
                                        }}
                                      >
                                        {currentSide === 'BUY' ? '↑' : '↓'}
                                      </Typography>
                                    </Box>
                                    <TextField
                                      value={displayVal}
                                      onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                      ) =>
                                        handleTradeChange(
                                          sa.id,
                                          h.key,
                                          h,
                                          currentSide,
                                          e.target.value,
                                        )
                                      }
                                      onFocus={() => setFocusedField(mk)}
                                      onBlur={() => setFocusedField(null)}
                                      placeholder='0.00'
                                      sx={{
                                        flex: 1,
                                        width: 0,
                                        minWidth: 0,
                                        '& .MuiInputBase-root': {
                                          height: 24,
                                          minHeight: 24,
                                          borderRadius: 0,
                                          bgcolor: 'transparent',
                                          '& fieldset': { border: 'none' },
                                          '& input::placeholder': {
                                            color: sys.color.onDisabled,
                                            opacity: 0.6,
                                          },
                                        },
                                        '& input': {
                                          ...VALUE_SX,
                                          textAlign: 'right',
                                          fontWeight: saHasTrade ? 700 : 400,
                                          p: '1px 4px',
                                          minWidth: 0,
                                          color: saIsBuy
                                            ? sys.color.positive
                                            : saIsSell
                                              ? sys.color.negative
                                              : sys.color.onSurface,
                                        },
                                      }}
                                    />
                                    {saHasTrade && (
                                      <Box
                                        className='trade-clear-btn'
                                        onClick={() => {
                                          const next = new Map(tradeEntries);
                                          next.delete(mk);
                                          setTradeEntries(next);
                                        }}
                                        sx={{
                                          px: 0.25,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          userSelect: 'none',
                                          height: 24,
                                          flexShrink: 0,
                                          color: sys.color.onDisabled,
                                          opacity: focusedField === mk ? 1 : 0,
                                          transition: 'opacity 0.15s ease',
                                          ...BORDER_LEFT,
                                          '&:hover': {
                                            color: sys.color.negative,
                                            bgcolor: sys.color.negativeVariant,
                                          },
                                        }}
                                      >
                                        <CloseIcon sx={{ fontSize: 10 }} />
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>
                              {multiSA && showSAPost && (
                                <Box
                                  textAlign='left'
                                  sx={{
                                    ...(disabled && !locked
                                      ? { opacity: 0.3 }
                                      : {}),
                                    px: 0.5,
                                    ...(si < displaySAs.length - 1
                                      ? SA_SEPARATOR
                                      : {}),
                                  }}
                                >
                                  <Typography
                                    variant='labelSmall'
                                    sx={{
                                      ...TABULAR_SX,
                                      fontWeight: 600,
                                      color: sys.color.onSurface,
                                    }}
                                  >
                                    {disabled && !locked
                                      ? '—'
                                      : showShares
                                        ? fmtQty(saPostQty)
                                        : formatMoney(saPostVal)}
                                  </Typography>
                                </Box>
                              )}
                            </Fragment>
                          );
                        })}

                        <Box
                          px={0.5}
                          textAlign='right'
                          sx={OUTCOME_BORDER_LEFT}
                        >
                          <Typography
                            variant='labelSmall'
                            sx={{
                              fontWeight: hasTrade ? 700 : 400,
                              color: hasTrade
                                ? sys.color.onSurface
                                : sys.color.onSurfaceVariant,
                            }}
                          >
                            {showShares
                              ? fmtQty(postQty)
                              : formatMoney(postVal)}
                          </Typography>
                        </Box>
                        <Box px={0.5} textAlign='right'>
                          <Typography
                            variant='labelSmall'
                            sx={{
                              fontWeight: hasTrade ? 600 : 400,
                              color: hasTrade
                                ? sys.color.onSurface
                                : sys.color.onSurfaceVariant,
                            }}
                          >
                            {fmtPct(postPct)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}

                  <Box
                    display='grid'
                    gridTemplateColumns={GRID}
                    gap={0}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      bgcolor: sys.color.neutralHover,
                      borderTop: '1px solid',
                      borderTopColor: sys.color.outline,
                      borderBottom: '2px solid',
                      borderColor: sys.color.outlineVariant,
                      alignItems: 'center',
                    }}
                  >
                    <Box px={0.5}>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...LABEL_SX,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          color: sys.color.onSurfaceVariant,
                          letterSpacing: '0.04em',
                        }}
                      >
                        SUBTOTAL
                      </Typography>
                    </Box>
                    <Box px={0.5} textAlign='right'>
                      <Typography variant='labelSmall' sx={BOLD_SX}>
                        {formatMoney(gBaseVal)}
                      </Typography>
                    </Box>
                    <Box px={0.5} textAlign='right'>
                      <Typography variant='labelSmall' sx={BOLD_SX}>
                        {fmtPct(gCurPct)}
                      </Typography>
                    </Box>
                    <Box px={0.5} textAlign='right'>
                      <Typography variant='labelSmall' sx={BOLD_SX}>
                        {fmtPct(gTgtPct)}
                      </Typography>
                    </Box>
                    <Box px={0.5} textAlign='right' sx={{ ...BORDER_RIGHT }}>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...BOLD_SX,
                          color: driftColor(c, Math.abs(gDrift)),
                        }}
                      >
                        {gDrift > 0 ? '+' : ''}
                        {gDrift.toFixed(1)}
                      </Typography>
                    </Box>
                    {displaySAs.map((sa, si) => (
                      <Fragment key={sa.id}>
                        <SACellTrade value={gSATrade[si]} />
                        {multiSA && showSAPost && (
                          <SAPostCell
                            value={gSAPost[si]}
                            showBorder={si < displaySAs.length - 1}
                          />
                        )}
                      </Fragment>
                    ))}
                    <Box px={0.5} textAlign='right' sx={OUTCOME_BORDER_LEFT}>
                      <Typography variant='labelSmall' sx={BOLD_SX}>
                        {formatMoney(gPostVal)}
                      </Typography>
                    </Box>
                    <Box px={0.5} textAlign='right'>
                      <Typography variant='labelSmall' sx={BOLD_SX}>
                        {fmtPct(gPostPct)}
                      </Typography>
                    </Box>
                  </Box>
                </Fragment>
              );
            })}

            <Box
              display='grid'
              gridTemplateColumns={GRID}
              gap={0}
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                bgcolor: sys.color.neutralHover,
                borderTop: '3px solid',
                borderColor: sys.color.primary,
                px: 1.5,
                py: 0.75,
                alignItems: 'center',
                boxShadow: `0 -4px 8px ${sys.color.neutralPressed}`,
              }}
            >
              <Box px={0.5}>
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...LABEL_UPPER_SX,
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                  }}
                >
                  TOTAL
                </Typography>
              </Box>
              <Box px={0.5} textAlign='right'>
                <Typography
                  variant='labelSmall'
                  sx={{ ...VALUE_SX, ...BOLD_SX }}
                >
                  {formatMoney(totals.curC)}
                </Typography>
              </Box>
              <Box px={0.5} textAlign='right'>
                <Typography variant='labelSmall' sx={BOLD_SX}>
                  100.0%
                </Typography>
              </Box>
              <Box px={0.5} textAlign='right'>
                <Typography variant='labelSmall' sx={BOLD_SX}>
                  100.0%
                </Typography>
              </Box>
              <Box sx={BORDER_RIGHT} />
              {displaySAs.map((sa, si) => {
                let saTrade = 0;
                allHoldings.forEach((h) => {
                  saTrade +=
                    tradeEntries.get(`${sa.id}:${h.key}`)?.tradeAmountCents ||
                    0;
                });
                let saBase = 0;
                allHoldings.forEach((h) => {
                  saBase += getSAHolding(sa, h)?.valueCents || 0;
                });
                return (
                  <Fragment key={sa.id}>
                    <SACellTrade value={saTrade} />
                    {multiSA && showSAPost && (
                      <SAPostCell
                        value={Math.abs(saBase + saTrade)}
                        showBorder={si < displaySAs.length - 1}
                      />
                    )}
                  </Fragment>
                );
              })}
              <Box px={0.5} textAlign='right' sx={OUTCOME_BORDER_LEFT}>
                <Typography
                  variant='labelSmall'
                  sx={{ ...VALUE_SX, ...BOLD_SX }}
                >
                  {formatMoney(Math.abs(totals.postC))}
                </Typography>
              </Box>
              <Box px={0.5} textAlign='right'>
                <Typography variant='labelSmall' sx={BOLD_SX}>
                  100.0%
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>

        {hasAnyPendingTrades && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: sys.color.outlineVariant,
              bgcolor: sys.color.background,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <CollapsibleHeader
                open={activeTradesOpen}
                onClick={() => setActiveTradesOpen(!activeTradesOpen)}
                label={`Pending Trades (${activeSubTradeRequests.length})`}
                sys={sysForShared}
              />
              {activeTradesOpen && (
                <Box display='flex' alignItems='center' gap={0.5}>
                  <IconButton
                    onClick={handleVerifySTRs}
                    disabled={selectedSTRIds.size === 0 || verifying}
                    size='small'
                    label='Verify selected trades'
                  >
                    <CheckCircleIcon
                      sx={{
                        fontSize: 18,
                        color:
                          selectedSTRIds.size > 0
                            ? sys.color.positive
                            : sys.color.onDisabled,
                      }}
                    />
                  </IconButton>
                  <IconButton
                    onClick={handleCancelSelectedSTRs}
                    disabled={selectedSTRIds.size === 0 || cancelling}
                    size='small'
                    label='Cancel selected trades'
                  >
                    <CloseIcon
                      sx={{
                        fontSize: 18,
                        color:
                          selectedSTRIds.size > 0
                            ? sys.color.negative
                            : sys.color.onDisabled,
                      }}
                    />
                  </IconButton>
                </Box>
              )}
            </Box>

            {activeTradesOpen && (
              <Box sx={{ px: 2, pb: 1.5, overflow: 'auto' }}>
                <Box
                  display='grid'
                  gridTemplateColumns={PENDING_GRID}
                  columnGap='6px'
                  sx={{
                    borderBottom: '2px solid',
                    borderColor: sys.color.onSurface,
                    pb: 0.25,
                    mb: 0.25,
                    alignItems: 'center',
                    minWidth: 580,
                  }}
                >
                  <Box>
                    <Checkbox
                      checked={allSTRsSelected}
                      onChange={selectAllSTRs}
                      customStyle={{ transform: 'scale(0.75)' }}
                    />
                  </Box>
                  {[
                    { label: 'Security', align: 'left' },
                    { label: 'Side', align: 'center' },
                    { label: 'Amount', align: 'right' },
                    { label: 'Account', align: 'left' },
                    { label: 'Status', align: 'left' },
                    { label: 'Verified', align: 'center' },
                    { label: '', align: 'center' },
                  ].map((h) => (
                    <Typography
                      key={h.label || 'actions'}
                      variant='labelSmall'
                      sx={{
                        ...SECTION_HEADER_SX,
                        ...MICRO_SX,
                        letterSpacing: '0.03em',
                        textAlign: h.align,
                      }}
                    >
                      {h.label}
                    </Typography>
                  ))}
                </Box>
                {activeSubTradeRequests.map((str) => {
                  const isBuy = str.type?.includes('BUY');
                  const isSellAll = str.isSellAll;
                  const isSelected = selectedSTRIds.has(str.id);
                  const isInBatch = !!str.bulkTradeRun?.id;
                  return (
                    <Box
                      key={str.id}
                      display='grid'
                      gridTemplateColumns={PENDING_GRID}
                      columnGap='6px'
                      sx={{
                        py: 0.2,
                        borderBottom: '1px solid',
                        borderColor: sys.color.outlineVariant,
                        alignItems: 'center',
                        minWidth: 580,
                        borderLeft: '2px solid',
                        borderLeftColor: isInBatch
                          ? sys.color.onDisabled
                          : tradeSideColor(c, isBuy),
                        bgcolor: isInBatch
                          ? sys.color.background
                          : isSelected
                            ? sys.color.primaryVariant
                            : 'transparent',
                        opacity: isInBatch ? 0.6 : 1,
                        transition: 'background 0.1s ease',
                        '&:hover': {
                          bgcolor: isInBatch
                            ? sys.color.background
                            : sys.color.neutralHover,
                        },
                      }}
                    >
                      <Box>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => !isInBatch && toggleSTRSelect(str.id)}
                          disabled={isInBatch}
                          customStyle={{ transform: 'scale(0.75)' }}
                        />
                      </Box>
                      <Box display='flex' alignItems='center' gap={0.5}>
                        <Typography
                          variant='labelSmall'
                          sx={{ fontWeight: 600 }}
                        >
                          {str.financialProduct?.ticker || '—'}
                        </Typography>
                        <Typography
                          variant='labelSmall'
                          sx={{
                            ...MICRO_SX,
                            ...TRUNCATE_SX,
                            color: sys.color.onDisabled,
                          }}
                        >
                          {str.currency}
                        </Typography>
                      </Box>
                      <Box textAlign='center'>
                        <SideBadge
                          isBuy={isBuy}
                          label={
                            isSellAll ? 'SELL ALL' : isBuy ? 'BUY' : 'SELL'
                          }
                          sys={sysForShared}
                        />
                      </Box>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...TABULAR_SX,
                          fontWeight: 600,
                          textAlign: 'right',
                          color: tradeSideColor(c, isBuy),
                        }}
                      >
                        {formatMoney(Math.abs(str.moneyAllocatedCents))}
                      </Typography>
                      <Typography
                        variant='labelSmall'
                        sx={{ ...TRUNCATE_SX, ...LABEL_SX }}
                      >
                        {str.subAccount?.account?.type || '—'}
                      </Typography>
                      <Box>
                        {isInBatch ? (
                          <Typography
                            variant='labelSmall'
                            sx={{
                              ...MICRO_SX,
                              ...BOLD_SX,
                              letterSpacing: '0.04em',
                              px: 0.5,
                              py: '1px',
                              borderRadius: '3px',
                              bgcolor: sys.color.primaryVariant,
                              color: sys.color.primary,
                              display: 'inline-block',
                            }}
                          >
                            IN BATCH
                          </Typography>
                        ) : (
                          <Typography
                            variant='labelSmall'
                            sx={{
                              ...LABEL_SX,
                              color:
                                str.state === 'INITIATED'
                                  ? sys.color.warning
                                  : str.state === 'READY'
                                    ? sys.color.primary
                                    : str.state === 'REQUESTED'
                                      ? sys.color.positive
                                      : sys.color.onSurfaceVariant,
                            }}
                          >
                            {str.state}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        textAlign='center'
                        display='flex'
                        justifyContent='center'
                        alignItems='center'
                      >
                        {str.verifiedAt ? (
                          <CheckCircleIcon
                            sx={{ fontSize: 11, color: sys.color.positive }}
                          />
                        ) : (
                          <Typography
                            variant='labelSmall'
                            sx={{ ...MICRO_SX, color: sys.color.onDisabled }}
                          >
                            —
                          </Typography>
                        )}
                      </Box>
                      <Box textAlign='center'>
                        {isInBatch ? (
                          <Typography
                            variant='labelSmall'
                            sx={{ ...MICRO_SX, color: sys.color.onDisabled }}
                          >
                            🔒
                          </Typography>
                        ) : (
                          <IconButton
                            onClick={() => handleCancelSTR(str.id)}
                            disabled={cancelling}
                            size='small'
                            label='Cancel this trade'
                          >
                            <CloseIcon
                              sx={{ fontSize: 12, color: sys.color.negative }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {activityBySA.length > 0 &&
          activityBySA.some((a) => a.entries.length > 0) && (
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: sys.color.outlineVariant,
                bgcolor: sys.color.background,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 0.75,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  userSelect: 'none',
                  transition: 'background 0.1s ease',
                  '&:hover': { bgcolor: sys.color.neutralHover },
                }}
              >
                <CollapsibleHeader
                  open={activityOpen}
                  onClick={() => setActivityOpen(!activityOpen)}
                  label='Per-Account Activity'
                  sys={sysForShared}
                  badge={
                    <Typography
                      variant='labelSmall'
                      sx={{
                        ...MICRO_SX,
                        ...BOLD_SX,
                        bgcolor: sys.color.primaryVariant,
                        color: sys.color.primary,
                        borderRadius: '4px',
                        px: 0.5,
                        py: '1px',
                        lineHeight: 1.2,
                        minWidth: 14,
                        textAlign: 'center',
                      }}
                    >
                      {activityBySA.reduce(
                        (n, a) =>
                          n + a.entries.filter((e) => e.side !== '—').length,
                        0,
                      )}
                    </Typography>
                  }
                />
              </Box>
              {activityOpen && (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Box display='flex' flexWrap='wrap' gap={2}>
                    {activityBySA.map(
                      ({ sa, entries }) =>
                        entries.length > 0 && (
                          <Box
                            key={sa.id}
                            sx={{
                              flex: '1 1 300px',
                              maxWidth: 500,
                              bgcolor: sys.color.surface,
                              ...BORDERED_BOX_SX,
                              borderRadius: '6px',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.25,
                                py: 0.5,
                                bgcolor: sys.color.primaryVariant,
                                borderBottom: '1px solid',
                                borderColor: sys.color.outlineVariant,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant='labelSmall'
                                sx={{
                                  ...VALUE_SX,
                                  ...BOLD_SX,
                                  color: sys.color.primary,
                                }}
                              >
                                {sa.label}
                              </Typography>
                              {sa.accountBaseCurrency && (
                                <Typography
                                  variant='labelSmall'
                                  sx={{
                                    ...MICRO_SX,
                                    color: sys.color.onDisabled,
                                  }}
                                >
                                  · {sa.accountBaseCurrency}
                                </Typography>
                              )}
                            </Box>
                            <Box
                              display='grid'
                              gridTemplateColumns={ACTIVITY_GRID}
                              columnGap='4px'
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderBottom: '1px solid',
                                borderColor: sys.color.outlineVariant,
                                bgcolor: sys.color.background,
                              }}
                            >
                              {['Activity', 'Side', '$', 'Balance'].map(
                                (hdr, hi) => (
                                  <Typography
                                    key={hdr}
                                    variant='labelSmall'
                                    sx={{
                                      ...SECTION_HEADER_SX,
                                      ...MICRO_SX,
                                      letterSpacing: '0.03em',
                                      textAlign:
                                        hi === 0
                                          ? 'left'
                                          : hi === 1
                                            ? 'center'
                                            : 'right',
                                    }}
                                  >
                                    {hdr}
                                  </Typography>
                                ),
                              )}
                            </Box>
                            {entries.map((ent, ei) => (
                              <TradeEntryRow
                                key={ei}
                                ent={ent}
                                isLast={ei >= entries.length - 1}
                              />
                            ))}
                          </Box>
                        ),
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
      </Box>

      <TradePreviewDialog
        open={showTradePreview && !!tradePreviewData}
        onClose={() => setShowTradePreview(false)}
        previewData={tradePreviewData}
        onConfirm={() => {
          setShowTradePreview(false);
          onGenerateTrades(Array.from(tradeEntries.values()));
        }}
      />

      <Box
        sx={{
          px: 2,
          py: 0.5,
          borderTop: '1px solid',
          borderColor: sys.color.outlineVariant,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: sys.color.surface,
        }}
      >
        <StatusPill
          label='holdings'
          value={allHoldings.length}
          color={sys.color.onSurfaceVariant}
        />
        {tradeCount > 0 && (
          <>
            <StatusPill
              label={`trade${tradeCount !== 1 ? 's' : ''}`}
              value={tradeCount}
              color={sys.color.primary}
            />
            <StatusPill
              label={`buy${buyCount !== 1 ? 's' : ''}`}
              value={buyCount}
              color={sys.color.positive}
            />
            <StatusPill
              label={`sell${sellCount !== 1 ? 's' : ''}`}
              value={sellCount}
              color={sys.color.negative}
            />
          </>
        )}
        {hasAnyPendingTrades && (
          <StatusPill
            label='pending'
            value={activeSubTradeRequests.length}
            color={sys.color.warning}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default TradeBlotter;
